import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useOrdersStore } from '../store/ordersStore'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../components/ui/SafeIcon'

const { FiPackage, FiTruck, FiCheckCircle, FiClock, FiArrowLeft } = FiIcons

function OrderDetailsPage() {
  const { orderId } = useParams()
  const { getOrderById } = useOrdersStore()
  const order = getOrderById(orderId)

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Bestellung nicht gefunden</h2>
          <Link to="/orders" className="text-blue-600 hover:underline">
            Zu meinen Bestellungen
          </Link>
        </div>
      </div>
    )
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return FiCheckCircle
      case 'shipped': return FiTruck
      case 'processing': return FiPackage
      default: return FiClock
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'delivered': return 'text-green-600 bg-green-100'
      case 'shipped': return 'text-blue-600 bg-blue-100'
      case 'processing': return 'text-orange-600 bg-orange-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'delivered': return 'Geliefert'
      case 'shipped': return 'Versandt'
      case 'processing': return 'In Bearbeitung'
      case 'confirmed': return 'Bestätigt'
      default: return status
    }
  }

  return (
    <>
      <Helmet>
        <title>Bestellung #{orderId} | 1688Euro.com</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex items-center mb-6">
            <Link
              to="/orders"
              className="flex items-center text-gray-600 hover:text-gray-800 mr-4"
            >
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5 mr-2" />
              Zurück zu Bestellungen
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              Bestellung #{order.id}
            </h1>
          </div>

          {/* Order Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${getStatusColor(order.status)}`}>
                  <SafeIcon icon={getStatusIcon(order.status)} className="w-6 h-6" />
                </div>
                <div>
                  <h2 className="text-lg font-bold">Bestellstatus</h2>
                  <p className="text-gray-600">{getStatusText(order.status)}</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Bestelldatum</p>
                <p className="font-medium">{new Date(order.createdAt).toLocaleDateString('de-DE')}</p>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ 
                  width: order.status === 'confirmed' ? '25%' : 
                         order.status === 'processing' ? '50%' :
                         order.status === 'shipped' ? '75%' : '100%'
                }}
              ></div>
            </div>

            <div className="flex justify-between text-xs text-gray-600">
              <span>Bestätigt</span>
              <span>In Bearbeitung</span>
              <span>Versandt</span>
              <span>Geliefert</span>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Order Items */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-bold mb-4">Bestellte Artikel</h2>
              <div className="space-y-4">
                {order.items.map((item, index) => (
                  <div key={index} className="flex items-center border-b border-gray-100 pb-4 last:border-0">
                    <img
                      src={item.image}
                      alt={item.name?.de || item.name}
                      className="w-16 h-16 object-cover rounded border border-gray-200"
                    />
                    <div className="ml-4 flex-1">
                      <h3 className="font-medium text-sm line-clamp-2">
                        {item.name?.de || item.name}
                      </h3>
                      <p className="text-sm text-gray-600">Menge: {item.quantity}</p>
                      <p className="text-sm text-gray-600">Lieferant: {item.supplier?.name}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium">{(item.price * item.quantity).toFixed(2)} €</p>
                      <p className="text-sm text-gray-600">{item.price.toFixed(2)} €/Stück</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Order Summary */}
            <div className="space-y-6">
              {/* Shipping Address */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-bold mb-4">Lieferadresse</h2>
                <div className="text-sm">
                  <p className="font-medium">{order.buyer.name}</p>
                  <p>{order.buyer.contactPerson}</p>
                  <p>{order.buyer.address}</p>
                  <p>{order.buyer.zipCode} {order.buyer.city}</p>
                  <p>{order.buyer.country}</p>
                  <p className="mt-2">{order.buyer.email}</p>
                  <p>{order.buyer.phone}</p>
                </div>
              </div>

              {/* Payment & Shipping */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-bold mb-4">Zahlung & Versand</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Zahlungsart:</span>
                    <span>
                      {order.payment.method === 'bank_transfer' ? 'Überweisung' : 
                       order.payment.method === 'paypal' ? 'PayPal' : order.payment.method}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Versandart:</span>
                    <span>
                      {order.shipping.method === 'express' ? 'Express' : 'Standard'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Geschätzte Lieferzeit:</span>
                    <span>{order.shipping.estimatedDelivery}</span>
                  </div>
                </div>
              </div>

              {/* Order Total */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-bold mb-4">Zusammenfassung</h2>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Zwischensumme:</span>
                    <span>{order.totals.subtotal.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Versandkosten:</span>
                    <span>{order.totals.shipping.toFixed(2)} €</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 pt-2 font-bold">
                    <span>Gesamtsumme:</span>
                    <span>{order.totals.total.toFixed(2)} €</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default OrderDetailsPage